import {
    useEffect,
    useState
} from "react";
import {
    View
} from "react-native";
import type {
    BeforeInstallPromptEvent
} from "./type";
import type InstallPwaProps from "./type";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitTheme,
    Button,
    Text
} from "ncore-ui-kit";
import {
    DownloadIcon
} from "lucide-react-native";

const isIOS = () => {
    try {
        if (typeof navigator === "undefined") return false;
        const platform = navigator.platform || "";
        const userAgent = navigator.userAgent || "";
        return [
            "iPhone Simulator",
            "iPad Simulator",
            "iPod Simulator",
            "iPhone",
            "iPad",
            "iPod"
        ].includes(platform) || (userAgent.includes("Mac") && typeof document !== "undefined" && "ontouchend" in document);
    } catch {
        return false;
    }
};

const InstallPwa = ({
    spreadBehaviour = "free"
}: InstallPwaProps) => {
    const {
        radiuses,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const [
        showIosInstructions,
        setShowIosInstructions
    ] = useState(false);

    const [
        isStandalone,
        setIsStandalone
    ] = useState(false);

    const [
        deferredPrompt,
        setDeferredPrompt
    ] = useState<BeforeInstallPromptEvent | null>(null);

    useEffect(() => {
        try {
            const checkStandalone = () => {
                const isStand = (typeof window !== "undefined" && window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) || (typeof navigator !== "undefined" && "standalone" in navigator && (navigator as unknown as { standalone: boolean }).standalone);
                setIsStandalone(!!isStand);
            };

            checkStandalone();

            const handleBeforeInstallPrompt = (e: Event) => {
                e.preventDefault();
                setDeferredPrompt(e as BeforeInstallPromptEvent);
            };

            if (typeof window !== "undefined") {
                window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
                return () => {
                    window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
                };
            }
            return () => {};
        } catch (e) {
            console.error("PWA effect error:", e);
            return () => {};
        }
    }, []);

    if(!deferredPrompt && !isIOS()) {
        return null;
    }

    if(isStandalone) {
        return null;
    }

    const handleInstallClick = async () => {
        try {
            if(deferredPrompt && typeof deferredPrompt.prompt === "function") {
                deferredPrompt.prompt();
                const choice = await deferredPrompt.userChoice;
                if(choice && choice.outcome === "accepted") {
                    setDeferredPrompt(null);
                }
            } else if(isIOS()) {
                setShowIosInstructions(true);
            }
        } catch (e) {
            console.error("Install click error:", e);
            if(isIOS()) {
                setShowIosInstructions(true);
            }
        }
    };

    if(showIosInstructions) {
        return <View
            style={[
                stylesheet.infoCard,
                spreadBehaviour === "stretch" ? stylesheet.fullWidth : undefined,
                {
                    backgroundColor: colors.content.container.default,
                    borderColor: colors.content.border.subtle,
                    borderRadius: radiuses.md,
                    padding: spaces.spacingMd
                }
            ]}
        >
            <Text
                style={stylesheet.infoText}
            >
                Uygulamamızı yüklemek için Safari'de alt menüdeki "Paylaş" ikonuna tıklayıp "Ana Ekrana Ekle"yi seçin.
            </Text>
            <Button
                onPress={() => setShowIosInstructions(false)}
                spreadBehaviour="stretch"
                type="neutral"
                title="Kapat"
            />
        </View>;
    }

    return <View
        style={spreadBehaviour === "stretch" ? stylesheet.fullWidth : undefined}
    >
        <Button
            icon={({
                color,
                size
            }: { color: keyof NCoreUIKit.IconContentColors; size: number }) => <DownloadIcon
                color={colors.content.icon[color]}
                size={size}
            />}
            spreadBehaviour={spreadBehaviour}
            onPress={handleInstallClick}
            title="Uygulamayı Yükle"
            type="neutral"
        />
    </View>;
};
export default InstallPwa;
